/**
 * Destinos VIP coherentes en todo el sitio.
 *
 * Landing canónica: `/invitacion-premium` (DualCTAVideoLanding / Reels A/B).
 * CTA de pago: checkout directo según país (sin hop por `/membresia-vip`).
 *
 * Política operador 2026-06-14:
 *   AR  → URL MP con preapproval_plan_id (env cascade).
 *   resto → suscripción Stripe (`startVipMembershipVideoCheckout`) — canal `premium_invite`.
 *   UNKNOWN → `/checkout-channel-invite-router` (solo geo; no PI Basic).
 *   Sin URL MP en AR → caller oculta CTA o cae a `/invitacion-premium`.
 *
 * `/invitacion-premium` ≠ `/r/cumple` (birthday) ≠ `/checkout-channel-invite` orgánico (Basic).
 */

import { PREMIUM_INVITE_FLOW_NAME } from "@/config/bioTemplatePremiumFlow";
import { getMercadoPagoCheckoutVipArUrl } from "@/lib/mercadopagoArCheckoutUrl";

export const PREMIUM_INVITE_LANDING_PATH = "/invitacion-premium";

export type VipCheckoutGateway = "mercadopago" | "stripe_subscription" | "country_router";

/** Cómo cobrar membresía VIP según país (no confundir con channel_invite one-time). */
export function getVipCheckoutGateway(country: string): VipCheckoutGateway {
  const c = (country || "UNKNOWN").toUpperCase();
  // VIP AR: solo URL VIP (no cascade Basic → VIP). Comunidad / invitacion-premium.
  if (c === "AR" && getMercadoPagoCheckoutVipArUrl()) return "mercadopago";
  if (c && c !== "UNKNOWN") return "stripe_subscription";
  return "country_router";
}

export function isPremiumInviteLandingPath(pathname: string): boolean {
  return pathname.includes(PREMIUM_INVITE_LANDING_PATH);
}

/** Query params para etiquetar checkout VIP desde invitación premium (recovery / revenue). */
export function appendPremiumInviteCheckoutQuery(href: string, country: string): string {
  let url = appendCountryQuery(href, country);
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}flow=${encodeURIComponent(PREMIUM_INVITE_FLOW_NAME)}&landing=${encodeURIComponent(PREMIUM_INVITE_LANDING_PATH)}&plan_tier=vip`;
}

export function isPremiumInviteMembershipCheckout(searchParams: URLSearchParams): boolean {
  const flow = searchParams.get("flow")?.trim();
  const landing =
    searchParams.get("landing")?.trim() ||
    searchParams.get("ref")?.trim() ||
    searchParams.get("source")?.trim() ||
    "";
  if (flow === PREMIUM_INVITE_FLOW_NAME) return true;
  if (landing.includes("invitacion-premium")) return true;
  return false;
}

/** Rutas/hosts legacy que ya no deben usarse como destino VIP. */
const LEGACY_VIP_CHECKOUT_PATTERN =
  /fan\.|\/membresia-vip|\/checkout-vip/i;

export function isLegacyVipCheckoutUrl(url: string | null | undefined): boolean {
  if (!url?.trim()) return false;
  return LEGACY_VIP_CHECKOUT_PATTERN.test(url.trim());
}

/**
 * Href navegable para VIP (MP externo o router geo).
 * Internacional usa suscripción Stripe vía `startVipMembershipVideoCheckout` — retorna "".
 */
export function resolveDirectVipCheckoutUrl(country: string): string {
  const gateway = getVipCheckoutGateway(country);
  if (gateway === "mercadopago") return getMercadoPagoCheckoutVipArUrl();
  if (gateway === "country_router") {
    return appendPremiumInviteCheckoutQuery("/checkout-channel-invite-router", country);
  }
  return "";
}

/** Si el CTA VIP debe mostrarse (MP, suscripción Stripe o router geo). */
export function hasVipCheckoutCta(country: string): boolean {
  return getVipCheckoutGateway(country) !== "mercadopago" || Boolean(getMercadoPagoCheckoutVipArUrl());
}

function appendCountryQuery(href: string, country: string): string {
  const cc = country.toUpperCase();
  if (!cc || cc === "UNKNOWN" || href.startsWith("http")) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}country=${encodeURIComponent(cc)}`;
}

/** MercadoPago subscription / preapproval links (AR only). */
export function isMercadoPagoCheckoutHref(url: string | null | undefined): boolean {
  if (!url?.trim()) return false;
  return /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(url.trim());
}

/**
 * Href del CTA VIP en Reels / admin override:
 * 1) override admin si no es legacy **y es válido para el país**
 *    - AR: solo hrefs MercadoPago (vip_checkout_base_url global suele ser MP AR)
 *    - exterior: nunca MP — Stripe (startVipMembershipVideoCheckout); overrides no-MP OK
 * 2) checkout directo (+ country en rutas internas)
 * 3) stripe_subscription → "" (caller abre Stripe)
 *
 * Bug prod: `vip_checkout_base_url` = plan MP AR se aplicaba a GeoSim MX y
 * mostraba badge + checkout MercadoPago en exterior.
 */
export function resolvePremiumInviteVipCtaHref(
  country: string,
  adminOverride?: string | null,
): string {
  const gateway = getVipCheckoutGateway(country);
  const override = adminOverride?.trim();
  if (override && !isLegacyVipCheckoutUrl(override)) {
    const isMp = isMercadoPagoCheckoutHref(override);
    if (gateway === "mercadopago") {
      // AR: override MP ok; override no-MP se ignora → cae a env VIP MP
      if (isMp) return override;
    } else if (!isMp) {
      // Exterior: solo overrides no-MP (payment link Stripe custom, etc.)
      return override;
    }
    // Exterior + override MP → ignorar (Stripe abajo)
  }
  if (gateway === "mercadopago") {
    const mp = getMercadoPagoCheckoutVipArUrl();
    if (mp) return mp;
  }
  if (gateway === "country_router") {
    return appendPremiumInviteCheckoutQuery("/checkout-channel-invite-router", country);
  }
  // stripe_subscription: sin href — el caller dispara startVipMembershipVideoCheckout
  return "";
}

/** Fallback cuando falla la API de suscripción Stripe (antes: /membresia-vip). */
export function vipMembershipFanFallbackUrl(countryCode: string): string {
  const raw = (countryCode || "").toUpperCase().trim();
  const cc =
    !raw || raw === "UNKNOWN" || raw === "XX"
      ? "UNKNOWN"
      : raw.slice(0, 2);
  const href = resolvePremiumInviteVipCtaHref(cc);
  if (href) return href;
  if (cc === "UNKNOWN") {
    return appendPremiumInviteCheckoutQuery("/checkout-channel-invite-router", "UNKNOWN");
  }
  return appendCountryQuery(PREMIUM_INVITE_LANDING_PATH, cc);
}